import { toast } from "@/hooks/use-toast";
import { supabase } from "@/utils/supabase/client";

const createUtilStore = (set, get) => ({
  showToast: ({
    id,
    title,
    description,
    variant = "default",
    duration,
    icon,
  }) => {
    return toast({
      id,
      title,
      description,
      variant,
      duration,
      icon,
    });
  },

  // Fetch random tags
  fetchRandomTags: async (limit = 15, options = {}, retryCount = 0) => {
    try {
      let query = supabase
        .from("tags")
        .select("id, name, category, show_onboarding");

      // Apply show_onboarding filter if specified in options
      if (options.show_onboarding) {
        query = query.eq("show_onboarding", true);
      }

      const { data, error } = await query
        .limit(limit)
        .order("created_at", { ascending: false })
        .then((result) => {
          if (result.data) {
            return {
              ...result,
              data: result.data.sort(() => Math.random() - 0.5),
            };
          }
          return result;
        });

      if (error) throw error;

      // If we got empty data but no error, try again without the show_onboarding filter
      if (!data || data.length === 0) {
        console.warn(
          "No tags returned from the database with filters, trying without filters"
        );

        // Try to get any tags from the database, regardless of show_onboarding
        const fallbackResponse = await supabase
          .from("tags")
          .select("id, name, category, show_onboarding")
          .limit(limit * 2) // Get more tags to have a better selection
          .order("created_at", { ascending: false })
          .then((result) => {
            if (result.data) {
              return {
                ...result,
                data: result.data.sort(() => Math.random() - 0.5),
              };
            }
            return result;
          });

        if (
          !fallbackResponse.error &&
          fallbackResponse.data &&
          fallbackResponse.data.length > 0
        ) {
          // Take a random subset of tags from the database
          const randomTags = fallbackResponse.data.slice(0, limit);
          return {
            success: true,
            tags: randomTags,
          };
        }

        // If still no tags, retry with backoff
        if (retryCount < 2) {
          console.log(`Retrying fetchRandomTags, attempt ${retryCount + 1}`);
          return await get().fetchRandomTags(limit, options, retryCount + 1);
        }

        // Last resort: truly no tags in the database, inform the user
        return {
          success: false,
          tags: [],
          error: "No tags available in the database",
        };
      }

      return {
        success: true,
        tags: data,
      };
    } catch (error) {
      console.error("Error fetching tags:", error);

      // Retry logic for network errors
      if (retryCount < 2) {
        console.log(
          `Network error, retrying fetchRandomTags, attempt ${retryCount + 1}`
        );
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve(get().fetchRandomTags(limit, options, retryCount + 1));
          }, 1000); // Wait 1 second before retrying
        });
      }

      // Try to get any tags from the database as a last resort, without filters
      try {
        const lastResortResponse = await supabase
          .from("tags")
          .select("id, name, category, show_onboarding")
          .limit(limit * 2) // Get more tags for better selection
          .then((result) => {
            if (result.data) {
              return {
                ...result,
                data: result.data.sort(() => Math.random() - 0.5),
              };
            }
            return result;
          });

        if (
          !lastResortResponse.error &&
          lastResortResponse.data &&
          lastResortResponse.data.length > 0
        ) {
          // Take a random subset of the tags
          const randomTags = lastResortResponse.data.slice(0, limit);
          return {
            success: true,
            tags: randomTags,
          };
        }
      } catch (lastError) {
        console.error("Final attempt to fetch tags failed:", lastError);
      }

      toast({
        title: "Error",
        description: "Failed to load tags",
        variant: "destructive",
      });

      // Ultimate fallback - we really couldn't get any tags
      return {
        success: false,
        tags: [],
        error: "Failed to fetch tags after multiple attempts",
      };
    }
  },
});

export default createUtilStore;
